window.BreederHybridTreeVisualization = class BreederHybridTreeVisualization {
    constructor(containerId, treeData, breederStrainIds, translations, breederId) {
        this.container = document.getElementById(containerId);
        if (!this.container) return;

        this.treeData = treeData;
        this.breederId = breederId;
        this.breederStrainIdsSet = new Set(breederStrainIds || []);
        this.translations = translations;
        this.network = null;
        this.data = null;
        this.hybridCounts = {};
        this.edgesMap = new Map();
        this.parentMap = new Map();
        this.edgeIdsByNode = new Map();
        this.highlightedEdgeIds = new Set();

        this.init();
    }

    init() {
        this.buildMaps();
        this.initSearch();
    }

    buildMaps() {
        this.treeData.edges.forEach(edge => {
            if (!this.edgesMap.has(edge.from)) this.edgesMap.set(edge.from, []);
            this.edgesMap.get(edge.from).push(edge.to);

            if (edge.from !== this.breederId) {
                if (!this.parentMap.has(edge.to)) this.parentMap.set(edge.to, []);
                this.parentMap.get(edge.to).push(edge.from);
            }
        });

        this.treeData.nodes.forEach(node => {
            this.hybridCounts[node.id] = this.edgesMap.get(node.id)?.length || 0;
        });
    }

    initSearch() {
        const searchInput = document.getElementById('strain-search');
        const searchBtn = document.getElementById('search-btn');
        const searchResult = document.getElementById('search-result');

        if (!searchInput || !searchBtn || !searchResult) return;

        searchInput.placeholder = this.translations.searchPlaceholder || 'Search strain by name...';
        searchBtn.textContent = this.translations.search || 'Search';

        const performSearch = () => {
            const query = searchInput.value.trim().toLowerCase();
            if (!query) {
                searchResult.textContent = '';
                searchResult.style.display = 'none';
                return;
            }

            searchResult.style.display = 'block';

            const foundNode = this.treeData.nodes.find(node =>
                node.label && node.label.toLowerCase().includes(query)
            );

            if (foundNode) {
                searchResult.textContent = `${this.translations.found || 'Found'}: ${foundNode.label}`;
                searchResult.className = 'block mt-2 text-sm break-words text-green-600';
                this.focusOnNode(foundNode.id);
            } else {
                searchResult.textContent = this.translations.notFound || 'Strain not found';
                searchResult.className = 'block mt-2 text-sm break-words text-red-600';
            }
        };

        searchBtn.addEventListener('click', performSearch);
        searchInput.addEventListener('keypress', (e) => e.key === 'Enter' && performSearch());
    }

    focusOnNode(nodeId, scale = 1.5) {
        if (!this.network) return;
        const node = this.data.nodes.get(nodeId);
        if (!node) return;
        this.network.focus(nodeId, {
            scale,
            animation: { duration: 500, easingFunction: 'easeInOutQuad' }
        });
        this.highlightNode(nodeId);
    }

    createNetwork() {
        const layoutPrecomputed = !!this.treeData.layoutPrecomputed;
        const nodeCount = this.treeData.nodes.length;
        const nodeLevels = {};

        this.treeData.nodes.forEach(n => { nodeLevels[n.id] = n.level ?? 0; });

        const nodeStyle = (level, isBreeder, isOwn, hybridCount) => {
            if (isBreeder) return { size: 18, fontSize: 16, fontColor: '#ffff80', bg: '#ffff80', border: '#ffff80' };
            if (isOwn) return { size: 14, fontSize: 13, fontColor: '#ffff66', bg: '#ffff66', border: '#ffff66' };
            if (hybridCount > 20) {
                return { size: Math.max(4, 8 - Math.floor(hybridCount / 20)), fontSize: Math.max(10, 12 - level), fontColor: '#96b18d', bg: '#a3b1a0', border: '#96b18d' };
            }
            if (level === 1) return { size: 10, fontSize: 11, fontColor: '#96b18d', bg: '#a3b1a0', border: '#96b18d' };
            return { size: 6, fontSize: 10, fontColor: '#96b18d', bg: '#a3b1a0', border: '#96b18d' };
        };

        const nodesWithLevels = this.treeData.nodes.map(node => {
            const { level: oldLevel, ...nodeWithoutLevel } = node;
            const finalLevel = nodeLevels[node.id] ?? (node.id === this.breederId ? 0 : (this.breederStrainIdsSet.has(node.id) ? 1 : 2));
            const s = nodeStyle(finalLevel, node.id === this.breederId, this.breederStrainIdsSet.has(node.id), this.hybridCounts[node.id] || 0);
            return {
                ...nodeWithoutLevel,
                level: finalLevel,
                size: s.size,
                font: { size: s.fontSize, color: s.fontColor },
                color: { background: s.bg, border: s.border, highlight: '#ffff66' },
            };
        });

        this.data = {
            nodes: new vis.DataSet(nodesWithLevels),
            edges: new vis.DataSet(this.treeData.edges),
        };

        this.data.edges.get().forEach(edge => {
            if (!this.edgeIdsByNode.has(edge.from)) this.edgeIdsByNode.set(edge.from, []);
            if (!this.edgeIdsByNode.has(edge.to)) this.edgeIdsByNode.set(edge.to, []);
            this.edgeIdsByNode.get(edge.from).push(edge.id);
            this.edgeIdsByNode.get(edge.to).push(edge.id);
        });

        const options = {
            layout: {
                hierarchical: {
                    enabled: !layoutPrecomputed,
                    direction: 'UD',
                    sortMethod: 'directed',
                    levelSeparation: 150,
                    nodeSpacing: 150,
                    treeSpacing: 200,
                    blockShifting: true,
                    edgeMinimization: true,
                    parentCentralization: true,
                    shakeTowards: 'leaves',
                },
                improvedLayout: !layoutPrecomputed && nodeCount <= 400,
            },
            physics: { enabled: false },
            interaction: {
                dragNodes: false,
                dragView: true,
                zoomView: true,
                navigationButtons: false,
            },
            nodes: {
                shape: 'dot',
                font: { size: 11, face: 'Arial' },
                scaling: { min: 4, max: 18 },
                margin: 10,
            },
            edges: {
                arrows: { to: { enabled: true, scaleFactor: 1.2 } },
                smooth: false,
            },
        };

        this.network = new vis.Network(this.container, this.data, options);

        this.network.once('afterDrawing', () => {
            this.network.focus(this.breederId, {
                scale: 1.5,
                animation: { duration: 800, easingFunction: 'easeInOutQuad' },
            });
            const breederNode = this.data.nodes.get(this.breederId);
            if (breederNode) {
                this.updateDetails(breederNode, this.edgesMap.get(this.breederId) || []);
            }
        });

        this.setupEventHandlers();
    }

    setupEventHandlers() {
        this.network.on('click', (params) => {
            if (params.nodes.length > 0) {
                this.highlightNode(params.nodes[0]);
            } else {
                this.clearHighlight();
            }
        });

        this.network.on('doubleClick', (params) => {
            if (params.nodes.length > 0) {
                const node = this.data.nodes.get(params.nodes[0]);
                if (node?.url && node.url !== '#') {
                    window.location.href = node.url;
                }
            }
        });
    }

    highlightNode(nodeId) {
        const node = this.data.nodes.get(nodeId);
        if (!node) return;

        const newEdgeIds = new Set(this.edgeIdsByNode.get(nodeId) || []);
        const edgeUpdates = [];

        this.highlightedEdgeIds.forEach(id => {
            if (!newEdgeIds.has(id)) {
                edgeUpdates.push({ id, color: { color: '#4a5048', highlight: '#ffff66', opacity: 1.0 }, width: 0.5 });
            }
        });

        newEdgeIds.forEach(id => {
            edgeUpdates.push({ id, color: { color: '#ffff66', highlight: '#ffff66', opacity: 1.0 }, width: 0.8 });
        });

        if (edgeUpdates.length > 0) this.data.edges.update(edgeUpdates);
        this.highlightedEdgeIds = newEdgeIds;

        this.updateDetails(node, this.edgesMap.get(nodeId) || []);
    }

    clearHighlight() {
        const edgeUpdates = Array.from(this.highlightedEdgeIds).map(id => ({
            id, color: { color: '#4a5048', highlight: '#ffff66', opacity: 1.0 }, width: 0.5,
        }));
        if (edgeUpdates.length > 0) this.data.edges.update(edgeUpdates);
        this.highlightedEdgeIds.clear();
        const detailsDiv = document.getElementById('inner-details');
        if (detailsDiv) detailsDiv.innerHTML = '';
    }

    updateDetails(node, childIds = []) {
        const detailsDiv = document.getElementById('inner-details');
        if (!detailsDiv) return;

        const renderLinks = (ids) => {
            const seen = new Set();
            const links = ids.map(id => {
                const n = this.data.nodes.get(id);
                if (!n || seen.has(n.label)) return '';
                seen.add(n.label);
                return `<a href="${this.escape(n.url || '#')}" class="link">${this.escape(n.label)}</a>`;
            }).filter(Boolean);
            return links.length > 0 ? links.join(', ') : this.escape(this.translations.unknown);
        };

        if (node.id === this.breederId) {
            detailsDiv.innerHTML = `
                <div class="card">
                    <h3 class="mb-3">
                        ${this.escape(this.translations.centeredStrain || 'Breeder')}: <a href="${this.escape(node.url || '#')}" class="link">${this.escape(node.label)}</a>
                    </h3>
                    <div class="space-y-2">
                        <p><strong>${this.escape(this.translations.strains || 'Strains')}:</strong> ${renderLinks(childIds)}</p>
                    </div>
                </div>
            `;
            return;
        }

        const parentIds = [...new Set(this.parentMap.get(node.id) || [])];
        const nodeBreeder = node.breeder ? ` <span class="text-gray-600">(${this.escape(node.breeder)})</span>` : '';

        detailsDiv.innerHTML = `
            <div class="card">
                <h3 class="mb-3">
                    ${this.escape(this.translations.centeredStrain || 'Strain')}: <a href="${this.escape(node.url || '#')}" class="link">${this.escape(node.label)}</a>${nodeBreeder}
                </h3>
                <div class="space-y-2">
                    <p><strong>${this.escape(this.translations.parentStrain || 'Parent Strain')}:</strong> ${renderLinks(parentIds)}</p>
                    <p><strong>${this.escape(this.translations.hybrids || 'Hybrids')}:</strong> ${renderLinks(childIds)}</p>
                </div>
            </div>
        `;
    }

    escape(text) {
        if (text == null) return '';
        return String(text).replace(/[&<>"']/g, m => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[m]));
    }
}
